import { chain } from "lodash";
import { ITplArbetsorder } from "../../models/dtos";
import {
    ITplArbetsorderFilter,
    ITplDestineringFilter,
    ITplFilter,
    ITplFilterTypes,
    ITplHandelssortimentFilter,
    ITplValbaraDestineringarFilter
} from "./tpl-filter.models";

export class TplFilterContext {
    nameAssertList: string[] = [];
    arbetsorderFilters: ITplArbetsorderFilter[] = [];
    handelssortimentFilters: ITplHandelssortimentFilter[] = [];
    destineringFilters: ITplDestineringFilter[] = [];
    valbaraDestineringarFilters: ITplValbaraDestineringarFilter[] = [];

    constructor(readonly filterState: ITplFilterTypes = null) {
        // empty
        if (!filterState) {
            this.filterState = {} as ITplFilterTypes;
        }
    }

    flush() {
        const all = [
            ...this.arbetsorderFilters,
            ...this.handelssortimentFilters,
            ...this.destineringFilters,
            ...this.valbaraDestineringarFilters
        ];
        all.forEach(filter => {
            filter.flush();
        });

        all.forEach(filter => {
            filter.prepare();
        });
    }

    clear() {
        const all = [
            ...this.arbetsorderFilters,
            ...this.handelssortimentFilters,
            ...this.destineringFilters,
            ...this.valbaraDestineringarFilters
        ];
        all.forEach(filter => {
            filter.init({});
        });
    }

    matchAll(arbetsordrar: ITplArbetsorder[]): ITplArbetsorder[] {
        const activeArbetsorderFilters = this.arbetsorderFilters.filter(x => x.isActive());
        const hasActiveArbetsorderFilters = activeArbetsorderFilters.length > 0;

        const activeHandelssortimentFilter = this.handelssortimentFilters.filter(x => x.isActive());
        const hasActiveHandelssortimentFilter = activeHandelssortimentFilter.length > 0;

        const activeDestineringFilter = this.destineringFilters.filter(x => x.isActive());
        const hasActiveDestineringFilter = activeDestineringFilter.length > 0;

        const activeValbaraDestineringFilter = this.valbaraDestineringarFilters.filter(x => x.isActive());
        const hasActiveValbaraDestineringFilter = activeValbaraDestineringFilter.length > 0;

        return arbetsordrar.filter(arbetsorder => {
            const aomatch = !hasActiveArbetsorderFilters || activeArbetsorderFilters.every(filter => filter.match(arbetsorder));
            if (!aomatch) {
                return false;
            }

            const hasHandelssortiment = arbetsorder.handelssortiment?.length > 0;
            if (!hasHandelssortiment) {
                return !(hasActiveHandelssortimentFilter || hasActiveDestineringFilter || hasActiveValbaraDestineringFilter);
            }

            const hsMatch = arbetsorder.handelssortiment
                .some(hs => !hasActiveHandelssortimentFilter || activeHandelssortimentFilter.every(filter => filter.match(hs, arbetsorder)));

            if (!hsMatch) {
                return false;
            }

            const aktivaDestineringar = chain(arbetsorder.handelssortiment)
                .map(x => x.destineringar)
                .flatten()
                .value();

            const valbaraDestineringar = chain(arbetsorder.handelssortiment)
                .map(x => x.valbaraDestineringar)
                .flatten()
                .value();

            if (aktivaDestineringar?.length === 0 && valbaraDestineringar?.length === 0) {
                return !(hasActiveDestineringFilter || hasActiveValbaraDestineringFilter);
            }

            const aktivaDestineringarMatch = !hasActiveDestineringFilter ||
                aktivaDestineringar.some(destinering =>  activeDestineringFilter.every(filter => filter.match(destinering)));

            const valbaraDestineringarMatch = !hasActiveValbaraDestineringFilter ||
                valbaraDestineringar.some(destinering => activeValbaraDestineringFilter.every(filter => filter.match(destinering)));

            return aktivaDestineringarMatch && valbaraDestineringarMatch;
        });
    }

    deregister(namn: string, filter: ITplFilter) {
        const namnIdx = this.nameAssertList.indexOf(namn);
        this.nameAssertList.splice(namnIdx, 1);

        const aof = this.arbetsorderFilters.indexOf(filter as ITplArbetsorderFilter);
        this.arbetsorderFilters.splice(aof, 1);

        const hsf = this.handelssortimentFilters.indexOf(filter as ITplHandelssortimentFilter);
        this.handelssortimentFilters.splice(hsf, 1);

        const def = this.destineringFilters.indexOf(filter as ITplDestineringFilter);
        this.destineringFilters.splice(def, 1);

        const defValbara = this.valbaraDestineringarFilters.indexOf(filter as ITplValbaraDestineringarFilter);
        this.valbaraDestineringarFilters.splice(defValbara, 1);
    }

    addArbetsorderFilter(namn: string, filter: ITplArbetsorderFilter): void {
        this.registerFilterNamn(namn);
        this.arbetsorderFilters.push(filter);
    }

    addHandelssortimentFilter(namn: string, filter: ITplHandelssortimentFilter): void {
        this.registerFilterNamn(namn);
        this.handelssortimentFilters.push(filter);
    }

    addDestineringFilter(namn: string, filter: ITplDestineringFilter): void {
        this.registerFilterNamn(namn);
        this.destineringFilters.push(filter);
    }

    addValbaraDestineringarFilter(namn: string, filter: ITplValbaraDestineringarFilter): void {
        this.registerFilterNamn(namn);
        this.valbaraDestineringarFilters.push(filter);
    }

    isVaglagerActive(): boolean {
        return this.filterState.vaglager.length === 1 && this.filterState.vaglager[0] === "Ja";
    }

    private registerFilterNamn(namn: string) {
        if (this.nameAssertList.some(added => added === namn)) {
            throw new Error(`Ett filter är redan registrerat med samma namn: ${namn}`);
        }

        this.nameAssertList.push(namn);
    }
}
